export type ColorTheme = 'ocean' | 'amber' | 'azadi';
export type ThemeMode = 'light' | 'dark' | 'system';

export interface ThemeColors {
  background: string;
  foreground: string;
  card: string;
  cardForeground: string;
  popover: string;
  popoverForeground: string;
  primary: string;
  primaryForeground: string;
  secondary: string;
  secondaryForeground: string;
  muted: string;
  mutedForeground: string;
  accent: string;
  accentForeground: string;
  destructive: string;
  destructiveForeground: string;
  border: string;
  input: string;
  ring: string;
  radius: string;
}

export interface ColorThemeDefinition {
  id: ColorTheme;
  name: string;
  description: string;
  light: ThemeColors;
  dark: ThemeColors;
}

export const colorThemes: Record<ColorTheme, ColorThemeDefinition> = {
  ocean: {
    id: 'ocean',
    name: 'Ocean Blue',
    description: 'Calm and soothing blue waters',
    light: {
      background: '210 25% 98%',
      foreground: '215 35% 12%',
      card: '210 40% 97%',
      cardForeground: '215 35% 12%',
      popover: '210 40% 97%',
      popoverForeground: '215 35% 12%',
      primary: '210 100% 50%',
      primaryForeground: '0 0% 100%',
      secondary: '210 30% 92%',
      secondaryForeground: '215 35% 12%',
      muted: '210 25% 94%',
      mutedForeground: '215 18% 45%',
      accent: '210 80% 55%',
      accentForeground: '0 0% 100%',
      destructive: '0 84.2% 60.2%',
      destructiveForeground: '210 40% 98%',
      border: '210 22% 88%',
      input: '210 25% 90%',
      ring: '210 100% 50%',
      radius: '0.5rem',
    },
    dark: {
      background: '222 47% 4%',
      foreground: '210 40% 96%',
      card: '222 40% 8%',
      cardForeground: '210 40% 96%',
      popover: '222 40% 8%',
      popoverForeground: '210 40% 96%',
      primary: '210 100% 56%',
      primaryForeground: '222 47% 4%',
      secondary: '222 30% 13%',
      secondaryForeground: '210 40% 96%',
      muted: '222 25% 11%',
      mutedForeground: '215 25% 68%',
      accent: '200 95% 48%',
      accentForeground: '0 0% 100%',
      destructive: '0 75% 48%',
      destructiveForeground: '0 0% 100%',
      border: '222 25% 15%',
      input: '222 25% 15%',
      ring: '210 100% 56%',
      radius: '0.5rem',
    },
  },
  amber: {
    id: 'amber',
    name: 'Eternal Amber',
    description: 'Warm candlelight glow with golden embrace',
    light: {
      background: '45 20% 98%',
      foreground: '30 15% 12%',
      card: '42 35% 97%',
      cardForeground: '30 15% 12%',
      popover: '42 35% 97%',
      popoverForeground: '30 15% 12%',
      primary: '38 92% 50%',
      primaryForeground: '30 10% 5%',
      secondary: '43 47% 88%',
      secondaryForeground: '30 15% 12%',
      muted: '43 30% 92%',
      mutedForeground: '30 8% 42%',
      accent: '36 64% 57%',
      accentForeground: '30 10% 5%',
      destructive: '0 84.2% 60.2%',
      destructiveForeground: '210 40% 98%',
      border: '43 25% 87%',
      input: '43 25% 87%',
      ring: '38 92% 50%',
      radius: '0.5rem',
    },
    dark: {
      background: '30 15% 6%',
      foreground: '40 20% 94%',
      card: '30 12% 10%',
      cardForeground: '40 20% 94%',
      popover: '30 12% 10%',
      popoverForeground: '40 20% 94%',
      primary: '38 95% 55%',
      primaryForeground: '30 10% 5%',
      secondary: '30 10% 14%',
      secondaryForeground: '40 20% 94%',
      muted: '30 8% 12%',
      mutedForeground: '38 12% 65%',
      accent: '36 85% 48%',
      accentForeground: '30 10% 5%',
      destructive: '0 75% 48%',
      destructiveForeground: '0 0% 100%',
      border: '30 10% 15%',
      input: '30 10% 15%',
      ring: '38 95% 55%',
      radius: '0.5rem',
    },
  },
  azadi: {
    id: 'azadi',
    name: 'Azadi',
    description: 'Deep freedom blue with powerful presence',
    light: {
      background: '210 30% 98%',
      foreground: '210 100% 5%',
      card: '210 40% 97%',
      cardForeground: '210 100% 5%',
      popover: '210 40% 97%',
      popoverForeground: '210 100% 5%',
      primary: '210 100% 16%',
      primaryForeground: '0 0% 100%',
      secondary: '210 35% 92%',
      secondaryForeground: '210 100% 5%',
      muted: '210 30% 94%',
      mutedForeground: '210 50% 35%',
      accent: '210 70% 92%',
      accentForeground: '210 100% 16%',
      destructive: '0 84.2% 60.2%',
      destructiveForeground: '210 40% 98%',
      border: '210 28% 88%',
      input: '210 30% 90%',
      ring: '210 100% 16%',
      radius: '0.5rem',
    },
    dark: {
      background: '220 40% 6%',
      foreground: '210 20% 96%',
      card: '220 35% 10%',
      cardForeground: '210 20% 96%',
      popover: '220 35% 10%',
      popoverForeground: '210 20% 96%',
      primary: '210 90% 52%',
      primaryForeground: '0 0% 100%',
      secondary: '220 30% 14%',
      secondaryForeground: '210 20% 96%',
      muted: '220 25% 13%',
      mutedForeground: '215 15% 65%',
      accent: '210 100% 55%',
      accentForeground: '0 0% 100%',
      destructive: '0 75% 48%',
      destructiveForeground: '0 0% 100%',
      border: '220 25% 16%',
      input: '220 25% 16%',
      ring: '210 90% 52%',
      radius: '0.5rem',
    },
  },
};
